import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Container,
  Paper,
  Divider,
} from '@mui/material';
import { Search, Lock } from '@mui/icons-material';
import { FormatUtils } from '@mega-pokedex/shared';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Menu, 
  MenuItem, 
  Button, 
  ListItemIcon, 
  ListItemText,
  useTheme
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Brightness4 as Brightness4Icon, 
  Brightness7 as Brightness7Icon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import ImmersiveBackground from '../../components/ImmersiveBackground';
import AuthDialog from '../../components/AuthDialog';
import CollectionGrid from '../Collection/components/CollectionGrid';
import { enrichCardData } from '../../utils/cardEnrichment';

interface PublicCard {
  id: string;
  cardName: string;
  justtcgName?: string;
  justtcgId?: string;
  setName: string;
  setSeries?: string;
  number: string;
  condition: string;
  language: string;
  costBasis: number;
  currentValue: number;
  purchaseDate?: string;
  location?: string;
  imageUrl: string;
  rarity?: string;
  quantity: number;
  game: 'pokemon' | 'onepiece';
  tags: string[];
  ownershipId: string;
}

const PublicCollectionView: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeContext();
  const { user, isAuthenticated, signOut } = useAuth();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  // Parse URL parameters for filters
  const gameFilter = searchParams.get('games')?.split(',') || [
    'pokemon',
    'onepiece',
  ];
  const locationFilter = searchParams.get('locations')?.split(',') || [];
  const hidePrices = searchParams.get('hidePrices') === 'true';
  const hideQuantities = searchParams.get('hideQty') === 'true';

  // TODO: Fetch public collection data from backend
  // For now, using mock data structure
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collection, setCollection] = useState<PublicCard[]>([]);
  const [ownerUsername, setOwnerUsername] = useState<string>('');
  const [isPrivate, setIsPrivate] = useState(false);

  // Fetch public collection from backend
  React.useEffect(() => {
    const fetchPublicCollection = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const url = `${apiUrl}/api/public/collection/${userId}?${searchParams.toString()}`;
        
        console.log('[PublicCollectionView] Fetching from:', url);
        
        const response = await fetch(url);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('Collection not found');
            setIsPrivate(false);
            return;
          }
          if (response.status === 403) {
            setIsPrivate(true);
            return;
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('[PublicCollectionView] Received data:', data);
        
        setOwnerUsername(data.username || 'Card Collector');
        
        // Enrich card data with metadata
        const rawCollection = data.collection || [];
        
        // Map backend fields to match CardOwnershipBase interface expected by enrichCardData
        const mappedCollection = rawCollection.map((card: any) => ({
          ...card,
          cardId: card.id, // Backend sends 'id', enrichment expects 'cardId'
          setId: card.setName, // Backend sends 'setName', enrichment expects 'setId'
          purchasePrice: card.costBasis,
          notes: card.location,
        }));
        
        const pokemonCards = mappedCollection.filter((c: any) => c.game === 'pokemon');
        const onePieceCards = mappedCollection.filter((c: any) => c.game === 'onepiece');
        
        console.log('[PublicCollectionView] Enriching', pokemonCards.length, 'Pokemon and', onePieceCards.length, 'One Piece cards');
        
        const [enrichedPokemon, enrichedOnePiece] = await Promise.all([
          pokemonCards.length > 0 ? enrichCardData(pokemonCards, 'pokemon', userId) : Promise.resolve([]),
          onePieceCards.length > 0 ? enrichCardData(onePieceCards, 'onepiece', userId) : Promise.resolve([]),
        ]);
        
        const enrichedCollection = [...enrichedPokemon, ...enrichedOnePiece];
        console.log('[PublicCollectionView] Enriched', enrichedCollection.length, 'cards');
        
        setCollection(enrichedCollection);
        setIsPrivate(!data.isPublic);
      } catch (err) {
        console.error('[PublicCollectionView] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load collection');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchPublicCollection();
    }
  }, [userId, searchParams]);

  // Filter collection
  const filteredCollection = useMemo(() => {
    let filtered = collection;

    // Filter by search term
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.cardName.toLowerCase().includes(lower) ||
          c.setName.toLowerCase().includes(lower),
      );
    }

    // Filter by game
    if (gameFilter.length > 0) {
      filtered = filtered.filter((c) => gameFilter.includes(c.game));
    }

    // Filter by location
    if (locationFilter.length > 0) {
      filtered = filtered.filter(
        (c) => c.location && locationFilter.includes(c.location),
      );
    }

    return filtered;
  }, [collection, searchTerm, gameFilter, locationFilter]);

  // Calculate stats (respecting privacy settings)
  const totalCards = filteredCollection.reduce(
    (sum, c) => sum + (c.quantity || 1),
    0,
  );
  const totalValue = !hidePrices
    ? filteredCollection.reduce(
        (sum, c) => sum + (c.currentValue || 0) * (c.quantity || 1),
        0,
      )
    : null;

  const pokemonCount = filteredCollection.filter(
    (c) => c.game === 'pokemon',
  ).length;
  const onePieceCount = filteredCollection.filter(
    (c) => c.game === 'onepiece',
  ).length;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
          <Typography ml={2}>Loading collection...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || isPrivate) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <ImmersiveBackground />
        <Lock sx={{ fontSize: 60, mb: 2, color: 'text.secondary' }} />
        <Typography variant="h5" gutterBottom>
          {error || 'This collection is private'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The owner has not made this collection public.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 3 }}>
          Go Home
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ImmersiveBackground />
      
      {/* Header */}
      <AppBar position="fixed" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold', color: 'primary.main' }}>
            Chasing TCG
          </Typography>

          {/* Theme Toggle */}
          <IconButton onClick={toggleTheme} color="inherit" sx={{ mr: 2 }}>
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          {/* Auth Controls */}
          {isAuthenticated ? (
            <>
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <Avatar
                  sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}
                >
                  {user?.email?.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                <MenuItem disabled>
                  <Typography variant="body2">{user?.email}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => navigate('/settings')}>
                  <ListItemIcon>
                    <AccountIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Settings</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => navigate('/dashboard')}>
                    <ListItemIcon>
                    <AccountIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Dashboard</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    signOut();
                    setAnchorEl(null);
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Sign Out</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button color="primary" variant="outlined" startIcon={<LoginIcon />} onClick={() => setAuthDialogOpen(true)}>
              Sign In
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8 }}>
        <Container maxWidth="xl">
          {/* Header Section */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 2,
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(25, 118, 210, 0.05) 100%)' 
                : 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0.02) 100%)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Grid container alignItems="center" spacing={3}>
              <Grid item>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: 'primary.main',
                    fontSize: '2rem'
                  }}
                >
                  {ownerUsername.charAt(0).toUpperCase()}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                  {ownerUsername}'s Collection
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Viewing public collection • {collection.length} cards
                </Typography>
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {collection.some(c => c.game === 'pokemon') && (
                    <Chip label="Pokemon" color="primary" variant="outlined" />
                  )}
                  {collection.some(c => c.game === 'onepiece') && (
                    <Chip label="One Piece" color="secondary" variant="outlined" />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>


          {/* Active Filters Display */}
          {(gameFilter.length > 0 || locationFilter.length > 0) && (
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {gameFilter.map((game) => (
                <Chip
                  key={game}
                  label={game === 'pokemon' ? 'Pokémon TCG' : 'One Piece TCG'}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
              {locationFilter.map((loc) => (
                <Chip
                  key={loc}
                  label={`📍 ${loc}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {/* Summary Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={1} sx={{ bgcolor: 'background.paper' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Cards
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {totalCards}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {pokemonCount > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={1} sx={{ bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Pokémon Cards
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {pokemonCount}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {onePieceCount > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={1} sx={{ bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      One Piece Cards
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {onePieceCount}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {!hidePrices && totalValue !== null && (
              <Grid item xs={12} sm={6} md={3}>
                <Card elevation={1} sx={{ bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Collection Value
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {FormatUtils.formatCurrency(totalValue)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Search bar */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search in this collection..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search color="action" sx={{ mr: 1 }} />,
              }}
              sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
            />
          </Box>

          {/* Collection Grid */}
          <CollectionGrid
            cards={collection.filter(c => 
              c.cardName.toLowerCase().includes(searchTerm.toLowerCase()) || 
              c.setName.toLowerCase().includes(searchTerm.toLowerCase())
            ) as any}
            totalCards={collection.length}
            searchTerm={searchTerm}
            onMenuClick={() => {}} // Read-only view
          />
        </Container>
      </Box>

      {/* Auth Dialog */}
      <AuthDialog
        open={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
      />
    </Box>
  );
};

export default PublicCollectionView;
